import React, { useCallback, useMemo } from 'react';
import { FormHelperText } from '@mui/material';
import AutocompleteDropdown from 'components/common/autocompleteDropdown/AutocompleteDropdown';
import type { DeviceType, ReleaseVersion } from 'api/admin/firmware/firmware';
import { useReleaseVersions } from 'api/queries/useReleaseVersions';

type Option = { value: string | number | null; display: string };

type ReleaseVersionSelectProps = {
  id?: string;
  deviceType?: DeviceType | null;
  selectedRelease: ReleaseVersion | null;
  onChange: (release: ReleaseVersion | null) => void;
  label?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
};

const SCROLL_THRESHOLD_PX = 24;

export const ReleaseVersionSelect: React.FC<ReleaseVersionSelectProps> = ({
  id = 'release-version',
  deviceType,
  selectedRelease,
  onChange,
  label = 'Release Version',
  size = 'medium',
  disabled = false,
}) => {
  const { data, isError, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useReleaseVersions(deviceType);

  const releases = useMemo(
    () => (data?.pages ?? []).flatMap((page) => page.releases),
    [data?.pages],
  );

  const releaseOptions: Option[] = useMemo(
    () => releases.map((release) => ({ value: release.id, display: release.version })),
    [releases],
  );

  const selectedOption = useMemo(
    () =>
      selectedRelease ? { value: selectedRelease.id, display: selectedRelease.version } : undefined,
    [selectedRelease],
  );

  const handleChange = useCallback(
    (option: Option | null) => {
      const releaseId = option?.value ?? null;
      if (releaseId === null) {
        onChange(null);
        return;
      }

      const release = releases.find((item) => item.id === releaseId);
      onChange(release ?? null);
    },
    [onChange, releases],
  );

  const handleScroll = useCallback(
    (event: React.SyntheticEvent) => {
      const listbox = event.currentTarget as HTMLElement;
      const isNearBottom =
        listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - SCROLL_THRESHOLD_PX;

      if (isNearBottom && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  return (
    <>
      <AutocompleteDropdown
        id={id}
        selectedData={selectedOption}
        onChange={handleChange}
        label={label}
        selectData={releaseOptions}
        size={size}
        disabled={disabled}
        loading={isFetching}
        listboxProps={{ onScroll: handleScroll }}
      />
      {isError && (
        <FormHelperText error>
          Failed to load release versions.
        </FormHelperText>
      )}
    </>
  );
};
